<template>
  <main class="sales-page">
    <div class="page-top">
      <div>
        <h1>Sales Performance</h1>
        <p class="intro">See what sold, where, and what remains after refunds and fees.</p>
      </div>
    </div>

    <div v-if="capabilityState === 'loading'" class="state-panel" role="status">
      Checking access to sales reports…
    </div>
    <div
      v-else-if="capabilityState === 'error'"
      class="state-panel state-error"
      role="alert"
    >
      <h2>We could not check report access</h2>
      <p>Please try again.</p>
      <button type="button" class="secondary-button" @click="loadCapabilities">
        Retry
      </button>
    </div>
    <div v-else-if="capabilityState === 'unavailable'" class="state-panel">
      <h2>Sales Performance is not available</h2>
      <p>This view has not been enabled for your account.</p>
    </div>
    <SalesComparison
      v-else-if="comparisonOpen"
      :capabilities="capabilities"
      :initial-filters="applied"
      :tenant-label="tenantLabel"
      @close="closeComparison"
    />
    <template v-else>
      <div class="report-toolbar">
        <label class="toolbar-field">Period
          <select
            :value="periodPreset"
            :disabled="reportState === 'loading'"
            @change="setPeriodPreset($event.target.value)"
          >
            <option v-if="periodPreset === 'ceremony'" value="ceremony" disabled>Ceremony sales to date</option>
            <option v-for="[value, label] in presets" :key="value" :value="value">
              {{ label }}
            </option>
          </select>
        </label>
        <label class="toolbar-field">Timezone
          <select
            :value="filtersExpanded ? draft.timezone : applied.timezone"
            :disabled="reportState === 'loading'"
            @change="setTimezone($event.target.value)"
          >
            <option v-for="zone in timezones" :key="zone" :value="zone">
              {{ zone.replace(/_/g, ' ') }}
            </option>
          </select>
        </label>
        <button
          type="button"
          class="secondary-button scope-button"
          :aria-expanded="filtersExpanded"
          @click="toggleFilters"
        >
          {{ filtersExpanded ? (hasPendingChanges ? "Cancel changes" : "Close filters") : "Change filters" }}
        </button>
      </div>

      <form
        v-if="filtersExpanded"
        class="filter-panel"
        @submit.prevent="applyFilters"
      >
        <div class="filter-heading">
          <div>
            <h2>Change report scope</h2>
            <p>
              {{
                draft.mode === "ceremony"
                  ? "All payments and later adjustments for selected ceremonies through the cutoff."
                  : "Payments and adjustments are shown on the dates they occurred."
              }}
            </p>
          </div>
        </div>
        <div class="filter-grid">
          <label
            ><span>Report by</span
            ><select v-model="draft.mode" @change="setMode(draft.mode)">
              <option value="activity">Payment date</option>
              <option value="ceremony">Ceremony sales to date</option>
            </select></label
          >
          <label
            ><span>University</span
            ><select v-model="selectedUniversityKey">
              <option value="">All permitted universities</option>
              <option
                v-for="university in universityOptions"
                :key="`${university.tenant}-${university.slug}`"
                :value="`${university.tenant}:${university.slug}`"
              >
                {{ university.name }}
              </option>
            </select></label
          >
          <label
            ><span>Channel</span
            ><select v-model="draft.channel">
              <option value="all">Online and in person</option>
              <option value="online">Online</option>
              <option value="pos">In person</option>
            </select></label
          >
          <label v-if="draft.mode === 'ceremony'"
            ><span>Sales through</span
            ><input
              v-model="draft.cutoff"
              type="date"
              required
              :aria-label="`Sales through date ${draft.timezone}`"
          /></label>
          <template v-else-if="!draft.period"
            ><label
              ><span>From</span
              ><input
                v-model="draft.from"
                :aria-invalid="invalidDates ? 'true' : null"
                :aria-describedby="invalidDates ? 'sales-date-error' : null"
                type="date"
                required
                :aria-label="`From date ${draft.timezone}`" /></label
            ><label
              ><span>To</span
              ><input
                v-model="draft.to"
                :aria-invalid="invalidDates ? 'true' : null"
                :aria-describedby="invalidDates ? 'sales-date-error' : null"
                type="date"
                required
                :aria-label="`To date ${draft.timezone}`" /></label
          ></template>
          <p v-if="draft.mode === 'activity' && draft.period" class="period-help">
            Period: {{ draftPeriodLabel }} · {{ draft.timezone }}.
            Choose Custom dates in the Period menu to edit calendar dates.
          </p>
        </div>
        <details
          v-if="draft.university_slug && ceremonyOptions.length"
          class="ceremony-picker"
        >
          <summary>
            Ceremonies
            <span>{{
              draft.ceremony_ids.length
                ? `${draft.ceremony_ids.length} selected`
                : "All in scope"
            }}</span>
          </summary>
          <div class="ceremony-list">
            <label v-for="ceremony in ceremonyOptions" :key="ceremony.id"
              ><input
                v-model="draft.ceremony_ids"
                type="checkbox"
                :value="ceremony.id"
              /><span>{{ ceremony.name }}</span></label
            >
          </div>
        </details>
        <label v-if="capabilities.disputes_enabled" class="dispute-toggle"
          ><input v-model="draft.include_disputes" type="checkbox" /><span
            ><strong>Include dispute movements</strong
            ><small
              >Includes dispute deductions, fees and reversals in totals and the
              evidence download.</small
            ></span
          ></label
        >
        <div class="filter-actions">
          <button
            type="submit"
            class="primary-button"
            :disabled="invalidDates || reportState === 'loading'"
          >
            Apply filters</button
          ><button type="button" class="text-button" @click="toggleFilters">
            Cancel</button
          ><button type="button" class="text-button" @click="resetFilters">
            Reset filters</button
          ><span v-if="invalidDates" id="sales-date-error" class="validation" role="alert"
            >The end date must be on or after the start date.</span
          >
        </div>
      </form>

      <div class="applied-bar">
        <span class="applied-label">Showing</span
        ><strong>{{ appliedDescription }}</strong
        ><span v-if="hasPendingChanges" class="pending-note"
          >Unapplied changes</span
        ><button
          ref="compareButton"
          type="button"
          class="secondary-button compare-button"
          :disabled="reportState !== 'ready' || hasPendingChanges"
          @click="comparisonOpen = true"
        >
          Compare
        </button>
        <button
          type="button"
          class="export-button"
          :disabled="
            !report || reportState !== 'ready' || hasPendingChanges || exporting
          "
          @click="exportCsv"
        >
          {{ exporting ? "Preparing CSV…" : "Download transactions (CSV)" }}
        </button>
        <span v-if="exportError" class="validation" role="alert">{{
          exportError
        }}</span>
      </div>

      <div v-if="reportState === 'loading'" class="state-panel" role="status">
        Loading sales figures…
      </div>
      <div
        v-else-if="reportState === 'error'"
        class="state-panel state-error"
        role="alert"
      >
        <h2>Sales figures could not be loaded</h2>
        <p>{{ reportError }}</p>
        <button type="button" class="secondary-button" @click="fetchReport">
          Retry
        </button>
      </div>
      <template v-else-if="report">
        <div
          v-if="report.coverage && report.coverage.status === 'not_synced'"
          class="notice notice-warning"
          role="status"
        >
          <strong>Sales data has not been imported for this selection yet.</strong
          ><span
            >No completed import covers these dates. Try another period or check
            back after the import completes.</span
          >
        </div>
        <div
          v-else-if="report.coverage && report.coverage.status === 'incomplete'"
          class="notice notice-warning"
          role="status"
        >
          <strong>Some sales data is incomplete.</strong
          ><span
            >Known amounts are shown, but totals may change as data
            arrives.</span
          >
        </div>
        <div
          v-if="report.freshness && report.freshness.stale"
          class="notice notice-warning"
          role="status"
        >
          <strong>Data refresh is delayed.</strong
          ><span
            >Latest successful import:
            {{ formatDateTime(report.freshness.last_successful_sync) }}.</span
          >
        </div>
        <div
          v-else-if="report.freshness && report.freshness.last_successful_sync"
          class="freshness"
        >
          Latest successful import
          {{ formatDateTime(report.freshness.last_successful_sync) }} · Report
          generated {{ formatDateTime(report.generated_at) }}
        </div>
        <div v-if="coverageDetails.length" class="coverage-detail">
          <strong>Amounts needing review</strong>
          <ul>
            <li v-for="(note, index) in coverageDetails" :key="index">
              {{ note }}
            </li>
          </ul>
        </div>

        <section
          v-if="!report.coverage || report.coverage.status !== 'not_synced'"
          class="overview-section"
          aria-labelledby="overview-title"
        >
          <div class="section-heading">
            <div>
              <h2 id="overview-title">Payments and refunds</h2>
              <p v-if="applied.mode === 'activity'">
                Payments and adjustments posted in this period. A refund may
                relate to an earlier purchase.
              </p>
            </div>
            <span class="basis-pill">{{
              applied.include_disputes
                ? "Including disputes"
                : "Excluding disputes"
            }}</span>
          </div>
          <div v-if="noMovements" class="empty-inline">
            <span>No payments or adjustments match this selection.</span>
            <button type="button" class="text-button" @click="openFilters">Choose another period</button>
          </div>
          <template v-else>
            <p v-if="currencyBasesDiffer" class="currency-note">
              Customer payments and payment processing may use different
              currencies. They describe the same activity; do not add the two
              panels together.
            </p>
            <div class="money-grid">
              <div class="money-panel">
                <div class="money-heading">
                  <h3>Customer payments</h3>
                  <p>Amounts customers paid, in their original currency</p>
                </div>
                <div
                  v-for="money in report.charge_currencies || []"
                  :key="money.currency"
                  class="currency-block"
                >
                  <div class="currency-title">{{ money.currency }}</div>
                  <dl>
                    <div class="headline-row">
                      <dt>Payments received</dt>
                      <dd>{{ formatMoney(money.sales_minor, money) }}</dd>
                    </div>
                    <div>
                      <dt>Refunds</dt>
                      <dd>
                        {{ formatDeduction(money.refunds_minor, money) }}
                      </dd>
                    </div>
                  </dl>
                </div>
                <p
                  v-if="!(report.charge_currencies || []).length"
                  class="muted"
                >
                  No customer payment amounts available.
                </p>
              </div>
              <div class="money-panel">
                <div class="money-heading">
                  <h3>Payment processing</h3>
                  <p>Amounts in the payment account currency. Bank payouts may arrive later.</p>
                </div>
                <div
                  v-for="money in report.settlement_currencies || []"
                  :key="money.currency"
                  class="currency-block"
                >
                  <div class="currency-title">
                    {{ money.currency }}
                    <span v-if="money.complete === false" class="incomplete-tag"
                      >Incomplete</span
                    >
                  </div>
                  <dl>
                    <div class="headline-row">
                      <dt>Gross payments</dt>
                      <dd>{{ formatMoney(money.payments_minor, money) }}</dd>
                    </div>
                    <div>
                      <dt>Refunds</dt>
                      <dd>
                        {{ formatDeduction(money.refunds_minor, money) }}
                      </dd>
                    </div>
                    <div>
                      <dt>Processing fees</dt>
                      <dd>
                        {{
                          formatDeduction(money.processing_fees_minor, money)
                        }}
                      </dd>
                    </div>
                    <template v-if="applied.include_disputes"
                      ><div>
                        <dt>Dispute deductions</dt>
                        <dd>
                          {{
                            formatDeduction(
                              money.dispute_deductions_minor,
                              money
                            )
                          }}
                        </dd>
                      </div>
                      <div>
                        <dt>Dispute fees</dt>
                        <dd>
                          {{ formatDeduction(money.dispute_fees_minor, money) }}
                        </dd>
                      </div>
                      <div>
                        <dt>Dispute reversals</dt>
                        <dd>
                          +{{
                            formatMoney(money.dispute_reversals_minor, money)
                          }}
                        </dd>
                      </div></template
                    >
                    <div class="net-row">
                      <dt>
                        {{ netLabel(money) }}
                      </dt>
                      <dd>{{ formatMoney(money.net_minor, money) }}</dd>
                    </div>
                  </dl>
                </div>
                <p
                  v-if="!(report.settlement_currencies || []).length"
                  class="muted"
                >
                  No payment account amounts available.
                </p>
              </div>
            </div>
            <h3 class="volume-heading">Orders and products sold</h3>
            <div class="count-grid">
              <div class="count-card">
                <span>Orders</span
                ><strong>{{
                  formatCount(report.counts && report.counts.orders)
                }}</strong
                ><small>Distinct paid purchases</small>
              </div>
              <div class="count-card">
                <span>Clips</span
                ><strong>{{
                  formatCount(report.counts && report.counts.clip_quantity)
                }}</strong
                ><small>Product quantity</small>
              </div>
              <div class="count-card">
                <span>Digital downloads</span
                ><strong>{{
                  formatCount(
                    report.counts && report.counts.digital_download_quantity
                  )
                }}</strong
                ><small>Product quantity</small>
              </div>
              <div class="count-card">
                <span>Snapshot products</span
                ><strong>{{
                  formatCount(report.counts && report.counts.snapshot_quantity)
                }}</strong
                ><small>Purchased units; bundles count as one</small>
              </div>
            </div>
            <details v-if="trendRows.length" class="trend-disclosure">
              <summary>View orders over time</summary>
              <div class="trend-card">
                <div>
                  <h3>Orders over time</h3>
                  <p>
                    Paid purchases by
                    {{ report.trend.granularity === "month" ? "month" : "day" }}
                    · counts, across all currencies
                  </p>
                </div>
                <div
                  class="trend-bars"
                  role="img"
                  :aria-label="trendDescription"
                >
                  <div
                    v-for="row in trendRows"
                    :key="row.date"
                    class="trend-column"
                  >
                    <div
                      class="trend-bar"
                      :style="{
                        height: `${Math.round((row.orders / trendMax) * 100)}%`,
                      }"
                      :title="`${row.date}: ${row.orders} orders`"
                    ></div>
                  </div>
                </div>
                <div class="trend-axis">
                  <span>{{ trendRows[0].date }}</span
                  ><span>{{ trendRows[trendRows.length - 1].date }}</span>
                </div>
                <details class="trend-data">
                  <summary>View order counts</summary>
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Date</th>
                        <th scope="col">Orders</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in trendRows" :key="row.date">
                        <td>{{ row.date }}</td>
                        <td>{{ row.orders }}</td>
                      </tr>
                    </tbody>
                  </table>
                </details>
              </div>
            </details>
          </template>
        </section>

        <section
          v-if="
            (!report.coverage || report.coverage.status !== 'not_synced') &&
            !noMovements
          "
          class="details-section"
          aria-labelledby="breakdown-title"
        >
          <div class="section-heading">
            <div>
              <h2 id="breakdown-title">Where sales came from</h2>
            </div>
          </div>
          <div class="detail-grid">
            <div class="detail-card">
              <template v-if="!applied.university_slug">
                <h3>By university <small>Most orders first</small></h3>
                <p v-if="!sortedUniversities.length" class="muted">
                  No universities have activity in this period.
                </p>
                <button
                  v-for="row in visibleUniversities"
                  :key="`${row.tenant}-${row.university_id}`"
                  type="button"
                  class="university-row"
                  :aria-label="`View sales for ${
                    row.university_name || row.university_slug
                  }`"
                  @click="selectUniversity(row)"
                >
                  <strong
                    >{{ row.university_name || row.university_slug }}
                  </strong>
                  <span class="breakdown-headline"
                    >{{ orderCountLabel(row.counts && row.counts.orders) }}
                    <small>{{ netSummary(row) }}</small></span
                  >
                  <span class="row-chevron" aria-hidden="true">›</span>
                </button>
                <button
                  v-if="universityLimit < sortedUniversities.length"
                  type="button"
                  class="text-button show-more"
                  @click="universityLimit += 10"
                >
                  Show more universities ({{
                    sortedUniversities.length - universityLimit
                  }}
                  remaining)
                </button>
              </template>
              <template v-else>
                <div class="selected-university-heading">
                  <h3>By ceremony <small>For {{ universityLabel(applied.tenant, applied.university_slug) }}</small></h3>
                  <button type="button" class="text-button" @click="clearUniversity">All universities</button>
                </div>
                <p v-if="!(report.by_ceremony || []).length" class="muted">
                  No ceremony activity in this period.
                </p>
                <details v-else class="ceremony-disclosure">
                  <summary>Break down by ceremony</summary>
                  <details
                    v-for="row in visibleCeremonies"
                    :key="`${row.tenant}-${row.ceremony_id}`"
                    class="breakdown"
                  >
                    <summary>
                      <span
                        ><strong>{{
                          row.ceremony_name || "Ceremony"
                        }}</strong
                        ><small>{{
                          formatDateTime(row.ceremony_datetime)
                        }}</small></span
                      ><span class="breakdown-headline"
                        >{{ orderCountLabel(row.counts && row.counts.orders) }}
                        <small>{{ netSummary(row) }}</small></span
                      >
                    </summary>
                    <div class="breakdown-money">
                      <div
                        v-for="money in row.charge_currencies || []"
                        :key="`charge-${money.currency}`"
                      >
                        <strong>{{ money.currency }} customer payments</strong
                        ><span
                          >Sales {{ formatMoney(money.sales_minor, money) }} ·
                          Refunds
                          {{ formatDeduction(money.refunds_minor, money) }}</span
                        >
                      </div>
                      <div
                        v-for="money in row.settlement_currencies || []"
                        :key="`settlement-${money.currency}`"
                      >
                        <strong>{{ money.currency }} payment processing</strong
                        ><span
                          >{{ netLabel(money) }}
                          {{ formatMoney(money.net_minor, money) }} · Fees
                          {{
                            formatDeduction(money.processing_fees_minor, money)
                          }}</span
                        >
                      </div>
                      <button
                        type="button"
                        class="text-button drill-button"
                        @click="selectCeremony(row)"
                      >
                        Show this ceremony
                      </button>
                    </div>
                  </details>
                  <button
                    v-if="ceremonyLimit < sortedCeremonies.length"
                    type="button"
                    class="text-button show-more"
                    @click="ceremonyLimit += 10"
                  >
                    Show more ceremonies ({{
                      sortedCeremonies.length - ceremonyLimit
                    }}
                    remaining)
                  </button>
                </details>
              </template>
            </div>
            <div class="detail-card">
              <h3>By channel</h3>
              <p v-if="!(report.by_channel || []).length" class="muted">
                No channel activity in this report.
              </p>
              <details
                v-for="row in report.by_channel || []"
                :key="row.channel"
                class="breakdown"
              >
                <summary>
                  <strong>{{ channelLabel(row.channel) }}</strong
                  ><span class="breakdown-headline"
                    >{{ orderCountLabel(row.counts && row.counts.orders) }}
                    <small>{{ netSummary(row) }}</small></span
                  >
                </summary>
                <div class="breakdown-money">
                  <div
                    v-for="money in row.charge_currencies || []"
                    :key="`charge-${money.currency}`"
                  >
                    <strong>{{ money.currency }} customer payments</strong
                    ><span
                      >Sales {{ formatMoney(money.sales_minor, money) }} ·
                      Refunds
                      {{ formatDeduction(money.refunds_minor, money) }}</span
                    >
                  </div>
                  <div
                    v-for="money in row.settlement_currencies || []"
                    :key="`settlement-${money.currency}`"
                  >
                    <strong>{{ money.currency }} payment processing</strong
                    ><span
                      >{{ netLabel(money) }}
                      {{ formatMoney(money.net_minor, money) }} · Fees
                      {{
                        formatDeduction(money.processing_fees_minor, money)
                      }}</span
                    >
                  </div>
                </div>
              </details>
              <h3 class="subheading">Products sold</h3>
              <div
                v-for="product in report.products || []"
                :key="product.product_type"
                class="product-row"
              >
                <div>
                  <strong>{{ productLabel(product.product_type) }}</strong
                  ><small
                    >{{ formatCount(product.quantity) }} sold<span
                      v-if="product.unknown_amount_count"
                    >
                      · {{ product.unknown_amount_count }} amount unknown</span
                    ></small
                  >
                </div>
                <div class="product-money">
                  <span
                    v-for="amount in product.currencies || []"
                    :key="amount.currency"
                    >{{ formatMoney(amount.sales_minor, amount) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </section>
      </template>
    </template>
    <router-link class="legacy-link" to="/admin/order-analytics">Legacy order analytics</router-link>
  </main>
</template>

<script>
import axios from "axios";
import SalesComparison from "./SalesComparison.vue";
import { presets, timezones, localDate, intervalLabel } from "../../services/salesPeriods.cjs";

const initialFilters = (tenant = "", timezone = "UTC") => {
  const anchor = new Date().toISOString();
  const today = localDate(anchor, timezone);
  return {
    tenant,
    timezone,
    period: "this-month",
    anchor,
    start_at: null,
    end_at: null,
    university_slug: "",
    ceremony_ids: [],
    mode: "activity",
    cutoff: today,
    from: `${today.slice(0, 8)}01`,
    to: today,
    channel: "all",
    include_disputes: true,
  };
};

export default {
  name: "SalesPerformance",
  components: { SalesComparison },
  data() {
    const filters = initialFilters();
    return {
      presets,
      timezones,
      comparisonOpen: false,
      capabilityState: "loading",
      capabilities: {},
      draft: { ...filters, ceremony_ids: [] },
      applied: { ...filters, ceremony_ids: [] },
      report: null,
      reportState: "idle",
      reportError: "",
      exporting: false,
      exportError: "",
      reportSerial: 0,
      filtersExpanded: false,
      universityLimit: 10,
      ceremonyLimit: 5,
    };
  },
  computed: {
    selectedUniversityKey: {
      get() {
        return this.draft.tenant && this.draft.university_slug
          ? `${this.draft.tenant}:${this.draft.university_slug}`
          : "";
      },
      set(key) {
        const university = this.universityOptions.find(
          (row) => `${row.tenant}:${row.slug}` === key
        );
        this.draft.university_slug = university ? university.slug : "";
        this.draft.ceremony_ids = [];
      },
    },
    periodPreset() {
      const filters = this.filtersExpanded ? this.draft : this.applied;
      return filters.mode === "activity" ? filters.period || "custom" : "ceremony";
    },
    draftPeriodLabel() {
      const preset = presets.find(([value]) => value === this.draft.period);
      return preset ? preset[1] : "Custom dates";
    },
    appliedPeriodDescription() {
      const f = this.applied;
      if (f.mode === "ceremony") return `Ceremony lifetime through ${this.formatUtcDate(f.cutoff)} · ${f.timezone}`;
      if (f.start_at && f.end_at) return `${intervalLabel(f, f.timezone)} · ${f.timezone}`;
      return `${this.formatUtcDate(f.from)} – ${this.formatUtcDate(f.to)} · ${f.timezone}`;
    },
    universityOptions() {
      return (this.capabilities.scopes || [])
        .filter(
          (scope) => scope.tenant === this.applied.tenant
        )
        .reduce(
          (all, scope) =>
            all.concat(
              (scope.universities || []).map((university) => ({
                ...university,
                tenant: scope.tenant,
              }))
            ),
          []
        );
    },
    ceremonyOptions() {
      return this.universityOptions
        .filter(
          (university) =>
            !this.draft.university_slug ||
            university.slug === this.draft.university_slug
        )
        .reduce(
          (all, university) =>
            all.concat(
              (university.ceremonies || []).map((ceremony) => ({
                ...ceremony,
                name: `${ceremony.name || university.name} · ${this.formatDateTime(
                  ceremony.datetime
                )}`,
              }))
            ),
          []
        );
    },
    invalidDates() {
      return (
        this.draft.mode === "activity" &&
        !this.draft.period &&
        !!this.draft.from &&
        !!this.draft.to &&
        this.draft.from > this.draft.to
      );
    },
    hasPendingChanges() {
      const draft = {
        ...this.draft,
        ceremony_ids: [...this.draft.ceremony_ids].sort(),
      };
      const applied = {
        ...this.applied,
        ceremony_ids: [...this.applied.ceremony_ids].sort(),
      };
      return JSON.stringify(draft) !== JSON.stringify(applied);
    },
    appliedDescription() {
      const parts = [this.appliedPeriodDescription];
      parts.push(this.tenantLabel(this.applied.tenant));
      parts.push(
        this.applied.university_slug
          ? this.universityLabel(
              this.applied.tenant,
              this.applied.university_slug
            )
          : "All permitted universities"
      );
      if (this.applied.ceremony_ids.length)
        parts.push(`${this.applied.ceremony_ids.length} ceremonies`);
      if (this.applied.channel !== "all")
        parts.push(this.channelLabel(this.applied.channel));
      parts.push(
        this.applied.include_disputes
          ? "including disputes"
          : "excluding disputes"
      );
      return parts.join(" · ");
    },
    coverageDetails() {
      const coverage = this.report && this.report.coverage;
      if (!coverage) return [];
      const notes = [];
      if (coverage.missing_fees)
        notes.push(`${coverage.missing_fees} payment fee amounts pending`);
      if (coverage.unknown_line_amounts)
        notes.push(
          `${coverage.unknown_line_amounts} product amounts unknown`
        );
      if (coverage.unmapped_count)
        notes.push(`${coverage.unmapped_count} transactions need review`);
      return notes;
    },
    noMovements() {
      return (
        !!this.report &&
        this.report.has_activity === false &&
        this.report.coverage &&
        this.report.coverage.status === "complete"
      );
    },
    currencyBasesDiffer() {
      if (!this.report) return false;
      const customer = (this.report.charge_currencies || []).map(
        (money) => money.currency
      );
      const settlement = (this.report.settlement_currencies || []).map(
        (money) => money.currency
      );
      return (
        customer.length > 0 &&
        settlement.length > 0 &&
        (customer.some((currency) => !settlement.includes(currency)) ||
          settlement.some((currency) => !customer.includes(currency)))
      );
    },
    sortedCeremonies() {
      return [...((this.report && this.report.by_ceremony) || [])].sort(
        (a, b) =>
          Number((b.counts && b.counts.orders) || 0) -
            Number((a.counts && a.counts.orders) || 0) ||
          String(b.ceremony_datetime || "").localeCompare(
            String(a.ceremony_datetime || "")
          )
      );
    },
    sortedUniversities() {
      return [...((this.report && this.report.by_university) || [])].sort(
        (a, b) =>
          Number((b.counts && b.counts.orders) || 0) -
            Number((a.counts && a.counts.orders) || 0) ||
          String(a.university_name || a.university_slug || "").localeCompare(
            String(b.university_name || b.university_slug || "")
          )
      );
    },
    visibleUniversities() {
      return this.sortedUniversities.slice(0, this.universityLimit);
    },
    visibleCeremonies() {
      return this.sortedCeremonies.slice(0, this.ceremonyLimit);
    },
    trendRows() {
      return this.report &&
        this.report.trend &&
        Array.isArray(this.report.trend.rows)
        ? this.report.trend.rows
        : [];
    },
    trendMax() {
      return Math.max(1, ...this.trendRows.map((row) => row.orders));
    },
    trendDescription() {
      return `Orders over time, ${this.trendRows.length} ${
        this.report.trend.granularity === "month" ? "months" : "days"
      }, ${this.trendRows.reduce(
        (sum, row) => sum + row.orders,
        0
      )} orders. Exact values available in the table below.`;
    },
  },
  created() {
    this.loadCapabilities();
  },
  beforeDestroy() {
    this.reportSerial += 1;
  },
  methods: {
    closeComparison() {
      this.comparisonOpen = false;
      this.$nextTick(() => this.$refs.compareButton.focus());
    },
    toggleFilters() {
      if (this.filtersExpanded) {
        this.draft = {
          ...this.applied,
          ceremony_ids: [...this.applied.ceremony_ids],
        };
      }
      this.filtersExpanded = !this.filtersExpanded;
    },
    openFilters() {
      this.filtersExpanded = true;
      this.$nextTick(() =>
        this.$el.querySelector(".filter-panel").scrollIntoView({ block: "start" })
      );
    },
    async loadCapabilities() {
      this.capabilityState = "loading";
      try {
        const response = await axios.get("/analytics/orders/v2/capabilities");
        this.capabilities = response.data || {};
        if (
          !this.capabilities.api_enabled ||
          !this.capabilities.view_enabled ||
          (this.capabilities.scopes || []).length !== 1
        ) {
          this.capabilityState = "unavailable";
          return;
        }
        // The API derives this sole scope from the signed-in tenant. Refuse
        // legacy multi-tenant capabilities rather than choosing one silently.
        const tenant = this.capabilities.scopes[0].tenant;
        this.draft.tenant = tenant;
        this.applied.tenant = tenant;
        if (!this.capabilities.disputes_enabled) {
          this.draft.include_disputes = false;
          this.applied.include_disputes = false;
        }
        this.capabilityState = "ready";
        this.fetchReport();
      } catch (_) {
        this.capabilityState = "error";
      }
    },
    setMode(mode) {
      this.draft.mode = mode;
      this.draft.period = null;
      this.draft.start_at = this.draft.end_at = null;
    },
    setPeriodPreset(preset) {
      const current = this.filtersExpanded ? this.draft : this.applied;
      this.draft = {
        ...current,
        ceremony_ids: [...current.ceremony_ids],
        mode: "activity",
        period: preset === "custom" ? null : preset,
        anchor: new Date().toISOString(),
        start_at: null,
        end_at: null,
      };
      if (preset === "custom") {
        this.openFilters();
        this.$nextTick(() => this.$el.querySelector('input[type="date"]').focus());
      } else if (!this.filtersExpanded) this.applyFilters();
    },
    setTimezone(timezone) {
      const current = this.filtersExpanded ? this.draft : this.applied;
      this.draft = { ...current, timezone, start_at: null, end_at: null };
      if (!this.filtersExpanded) this.applyFilters();
    },
    resetFilters() {
      this.draft = initialFilters(this.applied.tenant, this.applied.timezone);
      if (!this.capabilities.disputes_enabled)
        this.draft.include_disputes = false;
      this.applyFilters();
    },
    applyFilters() {
      if (
        this.invalidDates ||
        (this.draft.mode === "activity" && !this.draft.period && (!this.draft.from || !this.draft.to))
      )
        return;
      this.applied = {
        ...this.draft,
        ceremony_ids: [...this.draft.ceremony_ids],
      };
      this.filtersExpanded = false;
      this.universityLimit = 10;
      this.ceremonyLimit = 5;
      this.fetchReport();
    },
    params() {
      const f = this.applied;
      const params = {
        mode: f.mode,
        timezone: f.timezone,
        channel: f.channel,
        include_disputes: String(f.include_disputes),
        include_ceremonies: String(
          Boolean(f.university_slug || f.ceremony_ids.length)
        ),
      };
      if (f.tenant) params.tenant = f.tenant;
      if (f.university_slug) params.university_slug = f.university_slug;
      if (f.ceremony_ids.length) params.ceremony_ids = f.ceremony_ids.join(",");
      if (f.mode === "ceremony") params.cutoff = f.cutoff;
      else if (f.period) {
        params.period = f.period;
        params.anchor = f.anchor;
      } else {
        params.from = f.from;
        params.to = f.to;
      }
      return params;
    },
    async fetchReport() {
      const serial = ++this.reportSerial;
      this.reportState = "loading";
      this.reportError = "";
      this.exportError = "";
      try {
        const response = await axios.get("/analytics/orders/v2", {
          params: this.params(),
        });
        if (serial !== this.reportSerial) return;
        this.report = response.data;
        const scope = this.report.scope || {};
        const syncDraft = !this.hasPendingChanges;
        // Preserve the selection and freeze the server-resolved window for labels/comparison.
        for (const key of ["from", "to", "start_at", "end_at"]) {
          if (scope[key] != null) {
            this.applied[key] = scope[key];
            if (syncDraft) this.draft[key] = scope[key];
          }
        }
        this.reportState = "ready";
      } catch (error) {
        if (serial !== this.reportSerial) return;
        this.report = null;
        this.reportState = "error";
        this.reportError =
          error.response && error.response.status === 403
            ? "Your access to this scope has changed. Reset the filters or contact an administrator."
            : "Check your connection and try again.";
      }
    },
    async exportCsv() {
      if (
        !this.report ||
        this.hasPendingChanges ||
        this.reportState !== "ready"
      )
        return;
      this.exporting = true;
      this.exportError = "";
      try {
        const response = await axios.get("/analytics/orders/v2/export", {
          params: this.params(),
          responseType: "blob",
        });
        const url = URL.createObjectURL(response.data);
        const anchor = document.createElement("a");
        anchor.href = url;
        const scope = `${this.applied.tenant}-${
          this.applied.university_slug || "all-universities"
        }`;
        const period =
          this.applied.mode === "ceremony"
            ? `through-${this.applied.cutoff}`
            : `${this.applied.from}-to-${this.applied.to}`;
        anchor.download = `sales-performance-${scope}-${period}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
      } catch (_) {
        this.exportError = "The CSV could not be prepared. Please try again.";
      } finally {
        this.exporting = false;
      }
    },
    formatCount(value) {
      return Number(value || 0).toLocaleString("en-GB");
    },
    orderCountLabel(value) {
      return `${this.formatCount(value)} ${
        Number(value) === 1 ? "order" : "orders"
      }`;
    },
    formatMoney(value, money) {
      if (value === null || value === undefined) return "Amount pending";
      const exponent = Number.isInteger(money.currency_exponent)
        ? money.currency_exponent
        : 2;
      const amount = Number(value) / Math.pow(10, exponent);
      try {
        return new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency: money.currency,
          minimumFractionDigits: exponent,
          maximumFractionDigits: exponent,
        }).format(amount);
      } catch (_) {
        return `${money.currency} ${amount.toFixed(exponent)}`;
      }
    },
    formatDeduction(value, money) {
      if (value === null || value === undefined) return "Amount pending";
      return Number(value || 0) === 0
        ? this.formatMoney(0, money)
        : value < 0
        ? `+${this.formatMoney(-value, money)}`
        : `−${this.formatMoney(value, money)}`;
    },
    formatDateTime(value) {
      if (!value) return "Not yet available";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? value
        : new Intl.DateTimeFormat("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            timeZone: this.applied.timezone,
            timeZoneName: "short",
          }).format(date);
    },
    formatUtcDate(value) {
      const date = new Date(`${value}T00:00:00Z`);
      return Number.isNaN(date.getTime())
        ? value
        : new Intl.DateTimeFormat("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
            timeZone: "UTC",
          }).format(date);
    },
    channelLabel(channel) {
      return channel === "pos"
        ? "In person"
        : channel === "online"
        ? "Online"
        : "All channels";
    },
    tenantLabel(tenant) {
      return tenant === "mygraduationfilm"
        ? "MyGraduationClip"
        : tenant === "gfpevents"
        ? "GFP Events"
        : tenant;
    },
    universityLabel(tenant, slug) {
      const scope = (this.capabilities.scopes || []).find(
        (entry) => entry.tenant === tenant
      );
      const university = ((scope && scope.universities) || []).find(
        (entry) => entry.slug === slug
      );
      return university ? university.name : slug;
    },
    productLabel(type) {
      return type === "digital_download"
        ? "Digital download"
        : type === "clip"
        ? "Clip"
        : type === "snapshot"
        ? "Snapshot photo product"
        : type === "snapshot_delivery"
        ? "Snapshot delivery"
        : type || "Product";
    },
    netLabel(money) {
      const label = this.applied.include_disputes
        ? "Net after fees and adjustments"
        : "Net before dispute adjustments";
      return money.complete === false ? `Known ${label.toLowerCase()} · incomplete` : label;
    },
    netSummary(row) {
      return (
        (row.settlement_currencies || [])
          .map(
            (money) =>
              `${this.netLabel(money)}: ${this.formatMoney(money.net_minor, money)}`
          )
          .join(" · ") || "No payment account amount"
      );
    },
    clearUniversity() {
      this.draft = {
        ...this.applied,
        university_slug: "",
        ceremony_ids: [],
      };
      this.applyFilters();
    },
    selectCeremony(row) {
      this.draft = {
        ...this.applied,
        university_slug: row.university_slug,
        ceremony_ids: [row.ceremony_id],
      };
      this.applyFilters();
      this.$nextTick(() =>
        this.$el
          .querySelector(".applied-bar")
          .scrollIntoView({ block: "start" })
      );
    },
    selectUniversity(row) {
      this.draft = {
        ...this.applied,
        university_slug: row.university_slug,
        ceremony_ids: [],
      };
      this.applyFilters();
      this.$nextTick(() =>
        this.$el
          .querySelector(".applied-bar")
          .scrollIntoView({ block: "start" })
      );
    },
  },
};
</script>

<style scoped>
.sales-page {
  max-width: 1260px;
  margin: 0 auto;
  padding: 8px 0 64px;
  color: #17263d;
}
.page-top,
.section-heading,
.filter-heading,
.applied-bar,
.filter-actions,
.breakdown-row,
.product-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}
.page-top {
  align-items: flex-start;
  margin-bottom: 18px;
}
.page-top h1 {
  font-size: clamp(26px, 2.5vw, 30px);
  line-height: 1.12;
  letter-spacing: -0.035em;
  font-weight: 750;
  margin: 0;
}
.intro {
  margin: 6px 0 0;
  color: #5d6b7f;
  max-width: 610px;
  line-height: 1.5;
}
.legacy-link {
  display: inline-block;
  color: #315f96;
  font-size: 13px;
  font-weight: 650;
  margin-top: 8px;
}
.legacy-link:hover {
  text-decoration: underline;
}
.report-toolbar {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}
.toolbar-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  color: #42556d;
  max-width: 100%;
}
.toolbar-field select {
  min-height: 41px;
  max-width: 100%;
  padding: 8px 10px;
  border: 1px solid #788a9f;
  border-radius: 8px;
  background: #fff;
  color: #182c48;
  font-size: 13px;
}
.period-help { grid-column: 1 / -1; font-size: 13px; color: #42556d; }
.scope-button {
  margin-left: auto;
}
.filter-panel,
.money-panel,
.detail-card {
  background: #fff;
  border: 1px solid #dce4ed;
  border-radius: 14px;
  box-shadow: 0 3px 15px #19385808;
}
.filter-panel {
  padding: 20px 24px;
  margin: 12px 0;
}
.filter-heading {
  align-items: flex-start;
  margin-bottom: 20px;
}
.filter-heading h2,
.section-heading h2 {
  margin: 0;
  font-size: 20px;
  letter-spacing: -0.02em;
}
.filter-heading p,
.section-heading p:not(.eyebrow) {
  color: #65758b;
  font-size: 13px;
  margin: 5px 0 0;
}
.timezone,
.basis-pill {
  white-space: nowrap;
  color: #4b6380;
  background: #edf3f9;
  border-radius: 100px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 700;
}
.filter-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}
.filter-grid label {
  display: flex;
  flex-direction: column;
  gap: 7px;
  font-size: 12px;
  font-weight: 700;
  color: #42556d;
}
.filter-grid select,
.filter-grid input {
  width: 100%;
  min-height: 41px;
  padding: 8px 10px;
  border: 1px solid #cbd6e3;
  border-radius: 8px;
  background: #fff;
  color: #182c48;
  font-size: 13px;
}
.ceremony-picker {
  margin-top: 18px;
  border-top: 1px solid #e9edf2;
  padding-top: 16px;
  font-size: 13px;
}
.ceremony-picker summary {
  cursor: pointer;
  font-weight: 700;
  color: #2d5d96;
}
.ceremony-picker summary span {
  color: #78889b;
  font-weight: 500;
  margin-left: 5px;
}
.ceremony-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
  margin-top: 13px;
  max-height: 175px;
  overflow: auto;
}
.ceremony-list label {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  padding: 8px;
  border: 1px solid #e7edf3;
  border-radius: 7px;
}
.ceremony-list input {
  margin-top: 3px;
}
.dispute-toggle {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding-top: 18px;
  margin-top: 16px;
  border-top: 1px solid #e9edf2;
  cursor: pointer;
}
.dispute-toggle input {
  margin-top: 3px;
  accent-color: #1c5aa0;
}
.dispute-toggle strong,
.dispute-toggle small {
  display: block;
}
.dispute-toggle strong {
  font-size: 13px;
}
.dispute-toggle small {
  color: #687b91;
  margin-top: 2px;
  font-size: 12px;
}
.filter-actions {
  justify-content: flex-start;
  margin-top: 20px;
}
.primary-button,
.secondary-button,
.export-button {
  border-radius: 8px;
  min-height: 39px;
  padding: 8px 15px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.primary-button {
  border: 1px solid #1a5c9f;
  color: white;
  background: #1e63a8;
}
.primary-button:hover {
  background: #154e89;
}
.secondary-button,
.export-button {
  border: 1px solid #bdcbd9;
  color: #224e82;
  background: #fff;
}
.secondary-button:hover,
.export-button:hover {
  background: #eef5fc;
}
.text-button {
  border: 0;
  background: none;
  color: #356697;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.validation {
  font-size: 12px;
  color: #a23434;
}
.applied-bar {
  min-height: 55px;
  flex-wrap: wrap;
  padding: 8px 1px 13px;
  font-size: 12px;
  color: #65758b;
}
.applied-bar strong {
  color: #253d59;
  font-size: 13px;
  font-weight: 600;
  margin-right: auto;
}
.applied-label {
  font-weight: 700;
  font-size: 12px;
}
.pending-note {
  color: #a45f14;
  font-weight: 700;
}
.export-button {
  white-space: nowrap;
}
.state-panel,
.notice {
  padding: 22px;
  border-radius: 12px;
  background: #f1f6fb;
  color: #375371;
  margin: 10px 0 22px;
}
.state-panel h2 {
  font-size: 18px;
  margin: 0 0 5px;
}
.state-panel p {
  margin: 0 0 12px;
}
.state-error {
  background: #fff5f3;
  color: #913d32;
}
.notice {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 16px;
  font-size: 13px;
  margin: 0 0 12px;
}
.notice-warning {
  color: #795015;
  background: #fff5dc;
  border: 1px solid #f4dfa9;
}
.freshness {
  font-size: 12px;
  color: #52657a;
  margin: 3px 0 16px;
}
.coverage-detail {
  color: #5e6875;
  font-size: 12px;
  background: #f7f9fc;
  border: 1px solid #e1e7ef;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 20px;
}
.coverage-detail ul {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 24px;
  margin: 3px 0 0;
  padding-left: 18px;
}
.overview-section,
.details-section {
  margin: 18px 0 34px;
}
.section-heading {
  margin-bottom: 12px;
  align-items: flex-end;
}
.count-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border-top: 1px solid #e0e7ee;
  border-bottom: 1px solid #e0e7ee;
}
.count-card {
  padding: 13px 16px;
  border-right: 1px solid #e0e7ee;
}
.count-card:first-child {
  padding-left: 0;
}
.count-card:last-child {
  border-right: 0;
}
.count-card span,
.count-card small,
.count-card strong {
  display: block;
}
.count-card span {
  font-size: 12px;
  color: #58708a;
  font-weight: 700;
}
.count-card strong {
  font-size: 21px;
  line-height: 1.15;
  margin: 8px 0 4px;
  letter-spacing: -0.04em;
}
.count-card small {
  font-size: 12px;
  color: #52657a;
}
.money-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 13px;
  margin-top: 10px;
}
.currency-note {
  border-left: 3px solid #8eb4d6;
  background: #f4f8fc;
  color: #38536c;
  font-size: 13px;
  line-height: 1.45;
  padding: 9px 12px;
  margin: 0 0 13px;
}
.volume-heading {
  font-size: 16px;
  margin: 27px 0 10px;
}
.money-panel {
  padding: 22px;
}
.money-heading {
  padding-bottom: 16px;
  border-bottom: 1px solid #e7edf3;
}
.money-heading h3 {
  font-size: 16px;
  margin: 0;
}
.money-heading p {
  margin: 4px 0 0;
  color: #52657a;
  font-size: 13px;
}
.currency-block {
  border-bottom: 1px solid #edf1f5;
  padding: 15px 0 4px;
}
.currency-block:last-child {
  border-bottom: 0;
}
.currency-title {
  font-weight: 800;
  color: #2c6198;
  font-size: 12px;
  letter-spacing: 0.08em;
}
.currency-block dl {
  margin: 9px 0 0;
}
.currency-block dl div {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  padding: 5px 0;
  font-size: 13px;
}
.currency-block dt {
  color: #52657a;
}
.currency-block dd {
  margin: 0;
  font-weight: 650;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.currency-block dl .headline-row {
  font-size: 15px;
  padding: 8px 0 10px;
}
.headline-row dd {
  font-size: 18px;
  color: #173f77;
  font-weight: 800;
}
.currency-block .net-row {
  border-top: 1px solid #dce6ef;
  margin-top: 7px;
  padding: 13px 0 10px;
  font-size: 16px;
}
.net-row dd {
  font-size: 20px;
}
.net-row dt,
.net-row dd {
  color: #143d70;
  font-weight: 800;
}
.incomplete-tag {
  font-size: 10px;
  letter-spacing: 0;
  color: #8b5c19;
  background: #fff0ce;
  border-radius: 4px;
  padding: 3px 5px;
}
.empty-inline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  color: #52657a;
  background: #f5f8fb;
  border: 1px dashed #cfdbe8;
  border-radius: 10px;
  padding: 21px;
  margin: 12px 0;
  font-size: 13px;
}
.muted {
  color: #52657a;
  font-size: 13px;
}
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px;
}
.detail-card {
  padding: 20px 22px;
}
.detail-card h3 {
  margin: 0 0 12px;
  font-size: 15px;
}
.detail-card h3 small {
  color: #52657a;
  font-size: 12px;
  font-weight: 500;
  margin-left: 6px;
}
.show-more {
  padding: 13px 0 2px;
}
.selected-university-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.selected-university-heading .text-button {
  white-space: nowrap;
}
.university-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  text-align: left;
  background: transparent;
  border: 0;
  border-top: 1px solid #edf1f5;
  padding: 12px 8px;
  cursor: pointer;
  color: inherit;
  font: inherit;
  font-size: 13px;
}
.university-row:hover,
.university-row:focus-visible {
  background: #f2f7fb;
}
.row-chevron {
  color: #2d5d96;
  font-size: 20px;
  line-height: 1;
}
.university-row:hover strong {
  color: #245e9a;
}
.university-row strong,
.university-row small {
  display: block;
}
.university-row small {
  color: #52657a;
  font-size: 12px;
  font-weight: 400;
  margin-top: 3px;
}
.ceremony-disclosure > summary {
  cursor: pointer;
  color: #2d5d96;
  font-weight: 700;
  font-size: 13px;
  padding: 12px 0;
}
.detail-card .subheading {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #edf1f5;
}
.breakdown-row,
.product-row {
  border-top: 1px solid #edf1f5;
  padding: 12px 0;
  font-size: 13px;
}
.breakdown-row strong,
.product-row strong {
  display: block;
  font-weight: 650;
}
.breakdown-row small,
.product-row small {
  display: block;
  font-size: 12px;
  color: #52657a;
  margin-top: 3px;
}
.breakdown-row > span {
  color: #315f91;
  font-weight: 700;
  white-space: nowrap;
}
.product-money {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.product-money span {
  display: block;
  color: #315f91;
  font-weight: 700;
}
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
button:focus-visible,
a:focus-visible,
summary:focus-visible,
input:focus-visible,
select:focus-visible {
  outline: 3px solid #66a3e1;
  outline-offset: 2px;
}
.trend-disclosure {
  margin-top: 18px;
  font-size: 13px;
  color: #2d5d96;
}
.trend-disclosure > summary {
  cursor: pointer;
  font-weight: 700;
}
.trend-card {
  margin-top: 12px;
  padding: 20px 22px 14px;
  background: #fff;
  border: 1px solid #dce4ed;
  border-radius: 12px;
}
.trend-card h3 {
  margin: 0;
  font-size: 15px;
}
.trend-card p {
  margin: 3px 0 15px;
  font-size: 12px;
  color: #52657a;
}
.trend-bars {
  display: flex;
  align-items: flex-end;
  height: 92px;
  gap: 2px;
  border-bottom: 1px solid #bfd0e1;
  background: repeating-linear-gradient(
    to top,
    #fff 0,
    #fff 28px,
    #edf2f7 29px,
    #fff 30px
  );
}
.trend-column {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex: 1;
  height: 100%;
  min-width: 1px;
}
.trend-bar {
  width: min(100%, 18px);
  background: #4b87c4;
  border-radius: 3px 3px 0 0;
}
.trend-axis {
  display: flex;
  justify-content: space-between;
  color: #52657a;
  font-size: 11px;
  margin-top: 5px;
}
.trend-data {
  margin-top: 9px;
  font-size: 11px;
  color: #48698b;
}
.trend-data summary {
  cursor: pointer;
}
.trend-data table {
  border-collapse: collapse;
  margin: 8px 0;
  width: 180px;
}
.trend-data td,
.trend-data th {
  padding: 4px 8px 4px 0;
  text-align: left;
  border-bottom: 1px solid #e7edf3;
}
.breakdown {
  border-top: 1px solid #edf1f5;
}
.breakdown summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  list-style: none;
  cursor: pointer;
  font-size: 13px;
}
.breakdown summary::-webkit-details-marker {
  display: none;
}
.breakdown summary strong,
.breakdown summary small {
  display: block;
}
.breakdown summary small {
  color: #52657a;
  font-size: 12px;
  margin-top: 3px;
}
.breakdown-headline {
  text-align: right;
  font-weight: 700;
  color: #315f91;
}
.breakdown-money {
  background: #f6f9fc;
  border-radius: 8px;
  padding: 11px 13px;
  margin: 0 0 10px;
}
.breakdown-money > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-bottom: 1px solid #e5edf4;
  padding: 6px 0;
  color: #5c6f83;
  font-size: 12px;
}
.breakdown-money > div:last-of-type {
  border: 0;
}
.breakdown-money strong {
  color: #263f5a;
}
.drill-button {
  padding: 8px 0 2px;
}
@media (max-width: 800px) {
  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .money-grid,
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 560px) {
  .sales-page {
    padding: 4px 0 45px;
  }
  .page-top {
    gap: 9px;
    flex-direction: column;
  }
  .intro {
    font-size: 13px;
  }
  .report-toolbar {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
  .toolbar-field {
    min-width: 0;
  }
  .toolbar-field select {
    width: 100%;
    min-width: 0;
  }
  .scope-button {
    grid-column: 1 / -1;
    justify-self: start;
    margin-left: 0;
  }
  .filter-panel {
    padding: 17px;
  }
  .filter-heading {
    flex-wrap: wrap;
  }
  .filter-grid {
    grid-template-columns: 1fr;
  }
  .ceremony-list {
    grid-template-columns: 1fr;
  }
  .filter-actions {
    flex-wrap: wrap;
  }
  .applied-bar {
    align-items: flex-start;
    gap: 7px;
  }
  .applied-bar strong {
    flex-basis: 100%;
    order: 1;
  }
  .applied-bar .pending-note {
    order: 2;
  }
  .applied-bar .compare-button {
    order: 3;
  }
  .applied-bar .export-button {
    order: 3;
    margin-left: auto;
  }
  .count-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 7px;
  }
  .count-card {
    padding: 12px 10px;
    border-bottom: 1px solid #e0e7ee;
  }
  .count-card:nth-child(2) {
    border-right: 0;
  }
  .count-card:nth-child(n + 3) {
    border-bottom: 0;
  }
  .count-card strong {
    font-size: 25px;
  }
  .count-card span {
    font-size: 11px;
  }
  .count-card small {
    font-size: 12px;
  }
  .money-panel,
  .detail-card {
    padding: 17px;
  }
  .section-heading {
    align-items: flex-start;
  }
}
</style>
